import api from './client';
import { STORAGE_KEYS } from '../constants/storage-keys';

export interface AuthUser {
  id: string;
  username: string;
  fullname: string;
  firmId: string;
  roleId?: string | null;
  roleName?: string | null;
  permissions?: string[];
  mfaEnabled?: boolean;
  /** Linked RegionalRepresentative (portal login), e.g. «Алматы РП» */
  representativeId?: string | null;
  representativeName?: string | null;
  representativeRegion?: string | null;
  representativeCities?: Array<{ id: string; fullname: string }>;
}

export interface LoginResponse {
  access_token: string;
  user: AuthUser;
  /** Admin/finance role without MFA — UI should push enrollment */
  mfa_setup_required?: boolean;
}

/** Login answered with an MFA challenge instead of tokens. */
export interface MfaChallengeResponse {
  mfa_required: true;
  mfa_token: string;
}

export type LoginResult = LoginResponse | MfaChallengeResponse;

export function isMfaChallenge(res: LoginResult): res is MfaChallengeResponse {
  return (res as MfaChallengeResponse).mfa_required === true;
}

export interface MfaSetupResponse {
  secret: string;
  otpauthUrl: string;
}

/**
 * Check if the currently logged-in user has a specific permission code.
 * Empty/missing permissions → deny (no legacy allow).
 */
export function hasPermission(code: string): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return false;
    const user: AuthUser = JSON.parse(raw);
    const perms = user.permissions;
    if (!Array.isArray(perms) || perms.length === 0) return false;
    return perms.includes(code);
  } catch {
    return false;
  }
}

/** True if user has at least one of the given permission codes. */
export function hasAnyPermission(codes: string[]): boolean {
  if (codes.length === 0) return true;
  return codes.some(code => hasPermission(code));
}

/** Permissions from localStorage (empty array if missing). */
export function getUserPermissions(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return [];
    const user: AuthUser = JSON.parse(raw);
    return Array.isArray(user.permissions) ? user.permissions : [];
  } catch {
    return [];
  }
}

/** Role name from the authenticated user snapshot. */
export function getStoredRoleName(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    const user: AuthUser = JSON.parse(raw);
    return user.roleName ?? null;
  } catch {
    return null;
  }
}

/** Linked RegionalRepresentative id from stored auth user (null if none). */
export function getStoredRepresentativeId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    const user: AuthUser = JSON.parse(raw);
    return user.representativeId ?? null;
  } catch {
    return null;
  }
}

/** Current tenant firmId from stored auth user (null if missing). */
export function getStoredFirmId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    const user: AuthUser = JSON.parse(raw);
    return user.firmId ?? null;
  } catch {
    return null;
  }
}

export const authApi = {
  login: (username: string, password: string, rememberMe = false) =>
    api.post<LoginResult>('/auth/login', { username, password, rememberMe }, { withCredentials: true }).then(r => r.data),

  /** Second login step: 6-digit TOTP or recovery code. */
  mfaVerify: (mfaToken: string, code: string) =>
    api.post<LoginResponse>('/auth/mfa/verify', { mfa_token: mfaToken, code }, { withCredentials: true }).then(r => r.data),

  mfaSetup: () =>
    api.post<MfaSetupResponse>('/auth/mfa/setup').then(r => r.data),

  mfaEnable: (code: string) =>
    api.post<{ recoveryCodes: string[] }>('/auth/mfa/enable', { code }).then(r => r.data),

  mfaDisable: (code: string) =>
    api.post<{ disabled: boolean }>('/auth/mfa/disable', { code }).then(r => r.data),

  refresh: () =>
    api.post<LoginResponse>('/auth/refresh', {}, { withCredentials: true }).then(r => r.data),

  logout: () =>
    api.post('/auth/logout', {}, { withCredentials: true }).catch(() => {}),

  me: () =>
    api.get<AuthUser>('/auth/me').then(r => r.data),
};
